import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  ChevronLeft,
  Moon,
  Sun,
  GraduationCap,
  LayoutDashboard,
  Video,
  Menu,
  X,
  ShoppingCart,
  BookOpen,
  ClipboardCheck,
  ShieldCheck,
  Users,
  Trophy,
  User as UserIcon,
  Bell,
  BellRing,
  Calendar,
  AlertCircle,
  Megaphone,
  FileText,
  WalletCards
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import api from "../services/api";
import { useBranding } from "../context/BrandingContext";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDark, setIsDark] = useState(localStorage.getItem("theme") === "dark");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [role, setRole] = useState("student");
  const { branding } = useBranding();

  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);

  useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      setCartCount(cart.length);
    };

    updateCartCount();
    window.addEventListener("storage", updateCartCount);
    // Custom event for same-window updates
    window.addEventListener("cartUpdated", updateCartCount);

    try {
      const token = localStorage.getItem("token");
      if (token) {
        const decoded = jwtDecode(token);
        setRole(decoded.role || "student");
      }
    } catch (err) {
      console.error("Error decoding token in Navbar", err);
    }

    return () => {
      window.removeEventListener("storage", updateCartCount);
      window.removeEventListener("cartUpdated", updateCartCount);
    };
  }, []);

  const fetchNotifs = () => {
    const tok = localStorage.getItem("token");
    if (!tok) return;
    api.get("/notifications")
      .then(res => setNotifs(res.data))
      .catch(() => { });
  };

  useEffect(() => {
    fetchNotifs();
    const ival = setInterval(fetchNotifs, 60000); // Refresh every minute
    return () => clearInterval(ival);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setNotifs(notifs.map(n => ({ ...n, is_read: true })));
    } catch { }
  };

  const getIconForNotif = (type) => {
    switch (type) {
      case "assignment": return <FileText size={14} className="text-blue-500" />;
      case "exam": return <ClipboardCheck size={14} className="text-rose-500" />;
      case "result": return <Trophy size={14} className="text-emerald-500" />;
      case "class": return <Video size={14} className="text-indigo-500" />;
      default: return <Megaphone size={14} className="text-amber-500" />;
    }
  };

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDark]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const showBackButton = location.pathname !== "/dashboard" && location.pathname !== "/teacher-dashboard" && location.pathname !== "/parent-dashboard";

  if (location.pathname.startsWith("/exam/")) {
    return null;
  }

  const menuItems = [
    {
      label: "Profile",
      icon: UserIcon,
      path: role === "student" ? "/student-profile" : "/profile"
    },
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      path: role === "teacher" ? "/teacher-dashboard" : role === "parent" ? "/parent-dashboard" : "/dashboard"
    },
    ...(role !== "parent" ? [{
      label: role === "teacher" ? "My Courses" : "Explore Courses",
      icon: BookOpen,
      path: role === "teacher" ? "/teacher-dashboard" : "/dashboard"
    }] : []),
    ...(role === "teacher" ? [{ label: "Manage Exams", icon: ClipboardCheck, path: "/teacher-exams" }] : []),
    ...(role === "student" ? [
      { label: "Online Exams", icon: ClipboardCheck, path: "/exams" },
      { label: "Subscription Plans", icon: WalletCards, path: "/subscription-plans" },
      { label: "Performance", icon: Trophy, path: "/my-results" }
    ] : []),
    ...(role === "parent" ? [{ label: "My Children", icon: Users, path: "/parent-dashboard" }] : []),
    ...(role !== "parent" ? [{ label: "Live Classes", icon: Video, path: "/live-classes" }] : []),
    ...(role !== "parent" ? [{ label: "Community", icon: Users, path: "#" }] : []),
    { label: "Achievements", icon: Trophy, path: "#" },
    ...(role === "admin" ? [{ label: "Admin Panel", icon: ShieldCheck, path: "/admin" }] : []),
  ];

  return (
    <nav className="sticky top-0 z-50 glass border-b border-slate-200 dark:border-slate-800">
      <div className="mx-auto max-w-[1500px] px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">

          {/* Logo & Back Button */}
          <div className="flex items-center gap-4">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate("/dashboard")}
            >
              {branding.logo ? (
                <div className="flex items-center justify-center h-11 max-w-[150px] overflow-hidden rounded-lg">
                  <img src={branding.logo} alt="Brand" className="h-full w-auto object-contain" />
                </div>
              ) : (
                <div className="bg-blue-600 p-1.5 rounded-lg overflow-hidden flex items-center justify-center h-10 w-10 shrink-0">
                  <GraduationCap className="text-white w-6 h-6" />
                </div>
              )}
              <span className="max-w-[9rem] truncate bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-lg font-black text-transparent dark:from-blue-400 dark:to-indigo-400 sm:max-w-[15rem] sm:text-xl">
                {branding.headerTitle || branding.instituteName || "Antigravity"}
              </span>
            </motion.div>

            {showBackButton && (
              <motion.button
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => navigate(-1)}
                className="hidden sm:flex items-center gap-1 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
              >
                <ChevronLeft size={20} />
                <span className="text-sm font-medium">Back</span>
              </motion.button>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">

            {/* Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotif(!showNotif)}
                className={`p-2 rounded-xl transition-colors relative ${showNotif ? 'bg-slate-100 dark:bg-slate-800' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
              >
                {notifs.some(n => !n.is_read) ? <BellRing className="animate-swing text-blue-600" size={22} /> : <Bell size={22} />}
                {notifs.filter(n => !n.is_read).length > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-rose-500 border-2 border-white dark:border-slate-900 rounded-full animate-pulse" />
                )}
              </button>

              <AnimatePresence>
                {showNotif && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowNotif(false)} />
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 z-20 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="px-5 py-4 border-b dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
                        <h4 className="font-black text-sm">Notifications</h4>
                        <button onClick={handleMarkAllRead} className="text-[10px] font-bold text-blue-600 hover:underline">Clear all</button>
                      </div>
                      <div className="max-h-[350px] overflow-y-auto custom-scrollbar">
                        {notifs.length === 0 ? (
                          <div className="p-8 text-center text-slate-400 text-xs font-medium">No updates yet.</div>
                        ) : (
                          notifs.map(n => (
                            <div key={n.id} className={`p-4 border-b dark:border-slate-800/50 flex gap-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${!n.is_read ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''}`}>
                              <div className="mt-1 w-7 h-7 rounded-lg bg-white dark:bg-slate-800 border dark:border-slate-700 flex items-center justify-center shadow-sm shrink-0">
                                {getIconForNotif(n.type)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs font-bold truncate ${!n.is_read ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>{n.title}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2 leading-relaxed">{n.message}</p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => navigate("/cart")}
              className="relative p-2 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white shadow-lg">
                  {cartCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />

            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(role === "student" ? "/student-profile" : "/profile")}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                <UserIcon size={18} />
                <span>Profile</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-slate-600 dark:text-slate-400"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute left-0 top-16 max-h-[calc(100vh-4rem)] w-full overflow-y-auto border-b border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-950 md:hidden"
          >
            <div className="px-4 pt-2 pb-6 space-y-2">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => { navigate(item.path); setIsMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${location.pathname === item.path
                    ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                    : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
                    }`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </button>
              ))}
              <div className="pt-4 border-t dark:border-slate-800">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <LogOut size={20} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
